import React, { useState } from 'react';
import { TypeItem } from '@/lib/stores/typesStore';
import { Button } from '@/components/ui/button';
import { Variable, Link, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface LinkedValueSelectorProps {
  variables: TypeItem[];
  currentVariableId: string;
  linkedVariableId?: string;
  linkedElementId?: string;
  onSelect: (variableId: string | undefined, elementId: string | undefined) => void;
}

export const LinkedValueSelector: React.FC<LinkedValueSelectorProps> = ({
  variables,
  currentVariableId,
  linkedVariableId,
  linkedElementId,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedVar, setSelectedVar] = useState<string | undefined>(linkedVariableId);

  const linkedVariable = variables.find(v => v.id === linkedVariableId);
  const linkedElement = linkedVariable?.data?.find(e => e.id === linkedElementId);
  const availableVariables = variables.filter(v => v.id !== currentVariableId);

  const handleVariableSelect = (varId: string) => {
    setSelectedVar(varId);
  };

  const handleElementSelect = (elementId: string) => {
    if (selectedVar) {
      onSelect(selectedVar, elementId);
      setOpen(false);
      setSelectedVar(linkedVariableId);
    }
  };

  const handleClear = () => {
    onSelect(undefined, undefined);
    setOpen(false);
    setSelectedVar(undefined);
  };

  const selectedVariable = selectedVar ? variables.find(v => v.id === selectedVar) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "h-8 justify-start gap-2 font-normal",
            linkedVariableId ? "text-primary" : "text-muted-foreground"
          )}
        >
          <Link className="h-3 w-3" />
          {linkedVariable && linkedElement ? (
            <span className="truncate max-w-[150px]">
              {linkedVariable.name}.{linkedElement.name}
            </span>
          ) : (
            <span>Link value...</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2 border-b">
          <p className="text-sm font-medium">Link to Variable Value</p>
          <p className="text-xs text-muted-foreground">
            Select a variable and then a value to link to
          </p>
        </div>

        {linkedVariableId && (
          <div className="p-2 border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Currently linked to: <strong>{linkedVariable?.name}.{linkedElement?.name}</strong>
              </span>
              <Button variant="ghost" size="sm" onClick={handleClear} className="h-6 px-2">
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        <div className="max-h-[300px] overflow-auto">
          {availableVariables.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No other variables available to link to
            </div>
          ) : !selectedVar ? (
            <div className="p-1">
              <p className="text-xs text-muted-foreground px-2 py-1">Select a variable:</p>
              {availableVariables.map((variable) => (
                <button
                  key={variable.id}
                  onClick={() => handleVariableSelect(variable.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
                >
                  <Variable className="h-4 w-4 text-primary" />
                  <span>{variable.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {variable.data?.length || 0} values
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-1">
              <div className="border-t my-1" />
              <p className="text-xs text-muted-foreground px-2 py-1">
                Select a value from <strong>{selectedVariable?.name}</strong>:
              </p>
              {selectedVariable?.data?.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  This variable has no values
                </div>
              ) : (
                selectedVariable?.data?.map((element) => (
                  <button
                    key={element.id}
                    onClick={() => handleElementSelect(element.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
                  >
                    <span className="w-4 h-4 rounded bg-secondary flex items-center justify-center text-xs">
                      •
                    </span>
                    <span>{element.name}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
